import { BookingPolicy } from "../../types/appointments/AppointmentTypes";

/**
 * Patient-side booking-window bounding (S8, ClickUp 86d3n994u).
 *
 * The backend is authoritative: healthcare generates slots only inside `booking_window_days` and
 * re-checks the same window on booking. This module only decides which calendar dates the patient
 * can SELECT, so the calendar cannot offer a date the clinic would refuse.
 *
 * Shared by the two booking wizards — the public `BookingForm` and the logged-in
 * `AppointmentBookingModal` — which must bound their calendars identically.
 */

/**
 * "Today", as a Date object whose Y/M/D components are read via the BROWSER's own local getters —
 * i.e. it is directly comparable to the `date` argument callers pass to `isWithinBookingWindow`
 * (itself constructed as a browser-local wall-clock date, e.g. `new Date(y, m, d)` in the calendar
 * grid). The CALENDAR DAY is derived from `timezone` — matching the backend's anchor
 * (`AppointmentPolicyService`) — via `Intl.DateTimeFormat`, the same `en-CA` pattern
 * `DateTimeUtils` already uses elsewhere in this repo; the resulting Y/M/D is then rebuilt as a
 * browser-local Date so every later `setDate`/`setHours` call in this file keeps working exactly
 * as before.
 *
 * Falls back to the browser's own local date when `timezone` is missing, or is not a timezone
 * `Intl` recognises (`Intl.DateTimeFormat` throws a `RangeError` on an invalid `timeZone`) — never
 * throws.
 */
const clinicTodayAsLocalDate = (timezone: string | undefined, now: Date): Date => {
	if (!timezone) return new Date(now);

	try {
		const clinicDateStr = new Intl.DateTimeFormat("en-CA", { timeZone: timezone }).format(now); // "YYYY-MM-DD"
		const [year, month, day] = clinicDateStr.split("-").map(Number);

		return new Date(year, month - 1, day);
	} catch {
		return new Date(now);
	}
};

/**
 * The last date the clinic accepts a booking for, or `null` when there is no usable policy.
 *
 * The `policy` block is absent on the 404 (appointment type not found), so a missing or
 * nonsensical (< 1) window means "we don't know" — NOT "zero days". Bounding to today on a failed
 * read would empty a working calendar, so we return `null` and leave the calendar unbounded; the
 * offered slots, which the backend already limited to the real window, remain the gate.
 *
 * Anchored on the CLINIC's calendar day (`policy.timezone`), not the browser's — the backend
 * (`AppointmentPolicyService`) generates and clamps slots against ITS OWN "today", and a patient
 * whose browser is behind the clinic can be on the browser's "day N" while it is already
 * clinic-day N+1. Anchoring on the browser's `now` instead would compute a window one calendar day
 * short of what the backend actually offered — silently hiding the last bookable day even though
 * `availableDates` already contains it with real, bookable slots (MINOR 1).
 */
export const bookingWindowEnd = (policy?: BookingPolicy | null, now: Date = new Date()): Date | null => {
	const windowDays = policy?.booking_window_days;

	if (!windowDays || windowDays < 1) return null;

	const end = clinicTodayAsLocalDate(policy?.timezone, now);
	end.setDate(end.getDate() + windowDays);
	end.setHours(23, 59, 59, 999);

	return end;
};

/**
 * Is this date inside the clinic's booking window?
 *
 * An unknown window admits every date (see `bookingWindowEnd`). The bound is inclusive of the last
 * day, so a clinic with `booking_window_days = 7` offers today + 7 days, never 6.
 */
export const isWithinBookingWindow = (date: Date, policy?: BookingPolicy | null, now: Date = new Date()): boolean => {
	const end = bookingWindowEnd(policy, now);

	if (!end) return true;

	return date.getTime() <= end.getTime();
};
